import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import * as RovingFocus from '@radix-ui/react-roving-focus';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { cn } from './cn';

export interface TextListEditorProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** Accessible name for the chip group and the input. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Controlled chips editor: type a value and press Enter (or comma) to add it; commit on blur.
 * The chip list is an accessible composite — a single tab stop with roving focus (arrow keys move
 * between chips, Home/End jump), Delete/Backspace removes the focused chip (the × is pointer-only).
 *
 * API/visual parity with `./TagsInput` so the two can be deduped once that component's PR lands;
 * this variant additionally fixes the keyboard-navigation a11y.
 */
export const TextListEditor = ({
  value,
  onChange,
  placeholder = 'Type and press Enter',
  disabled,
  id,
  ariaLabel,
  className,
}: TextListEditorProps) => {
  const [draft, setDraft] = React.useState('');
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const ulRef = React.useRef<HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Index of the <li> to focus after a removal re-render (-1 → focus the input, null → do nothing).
  const pendingFocusIndex = React.useRef<number | null>(null);

  React.useEffect(() => {
    const idx = pendingFocusIndex.current;
    if (idx === null) return;
    pendingFocusIndex.current = null;
    if (idx < 0) {
      inputRef.current?.focus();
    } else {
      ulRef.current?.querySelectorAll<HTMLLIElement>('li')[idx]?.focus();
    }
  }, [value]);

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (tag && !value.includes(tag)) {
      onChange([...value, tag]);
    }
    setDraft('');
  };

  const removeTagAt = (index: number, focusInput = false) => {
    const newValue = value.filter((_, i) => i !== index);
    pendingFocusIndex.current = focusInput || newValue.length === 0 ? -1 : Math.min(index, newValue.length - 1);
    onChange(newValue);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
    } else if (event.key === 'Backspace' && !draft && value.length > 0) {
      removeTagAt(value.length - 1, true);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-surface px-2 py-1.5 transition-all duration-200',
        'focus-within:border-info-500 focus-within:ring-2 focus-within:ring-info-500/20',
        disabled && 'cursor-not-allowed bg-gray-50 opacity-60',
        className
      )}
      onClick={() => !disabled && inputRef.current?.focus()}
      data-test="text-list-editor"
    >
      {value.length > 0 && (
        <RovingFocus.Root asChild loop>
          <ul ref={ulRef} role="list" aria-label={ariaLabel} className="flex flex-wrap items-center gap-1.5">
            <AnimatePresence initial={false}>
              {value.map((tag, index) => (
                <RovingFocus.Item key={tag} asChild focusable>
                  <motion.li
                    layout
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    transition={{ duration: 0.15 }}
                    aria-label={`${tag}, press Delete to remove`}
                    onKeyDown={(event) => {
                      if (event.key === 'Delete' || event.key === 'Backspace') {
                        event.preventDefault();
                        removeTagAt(index);
                      }
                    }}
                    className={cn(
                      'inline-flex cursor-default items-center gap-1 rounded-full bg-info-100 py-0.5 pl-2.5 pr-1 type-label text-info-700 outline-none',
                      'focus-visible:ring-2 focus-visible:ring-info-500'
                    )}
                  >
                    {tag}
                    {!disabled && (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`Remove ${tag}`}
                        // Keep focus/draft intact when removing by pointer (don't blur-commit a partial draft).
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeTagAt(index, true);
                        }}
                        className="flex h-4 w-4 items-center justify-center rounded-full border-none bg-transparent text-info-700 transition-colors duration-150 hover:bg-info-200"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    )}
                  </motion.li>
                </RovingFocus.Item>
              ))}
            </AnimatePresence>
          </ul>
        </RovingFocus.Root>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        disabled={disabled}
        value={draft}
        aria-label={ariaLabel}
        placeholder={value.length === 0 ? placeholder : ''}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleInputKeyDown}
        onBlur={() => addTag(draft)}
        className="min-w-32 flex-1 border-none bg-transparent px-1 py-0.5 text-sm text-text-primary outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
      />
      <button
        type="button"
        aria-label="Add"
        disabled={disabled || !draft.trim()}
        // Keep focus in the input so clicking Add doesn't blur-commit before this handler adds.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => addTag(draft)}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-none bg-transparent text-info-600 transition-colors duration-150 hover:bg-info-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-500 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );
};
